import React, { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import {
  Award, Trophy, Download, Linkedin, Facebook, Twitter, Instagram, Star, ArrowRight, Image as ImageIcon,
} from "lucide-react";
import { toast } from "sonner";
import { api, API_BASE } from "../lib/api";
import { downloadFromApi, downloadBlob } from "../lib/downloads";
import { fireBigConfetti } from "../lib/celebration";

export default function CourseComplete() {
  const navigate = useNavigate();
  const { courseSlug } = useParams();
  const [cert, setCert] = useState(null);
  const [course, setCourse] = useState(null);
  const [stars, setStars] = useState(0);
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [rated, setRated] = useState(false);

  useEffect(() => {
    // Fire celebration on mount
    setTimeout(fireBigConfetti, 200);
    // Resolve course + certificate
    api.get(`/courses/${courseSlug}`)
      .then((r) => setCourse(r.data))
      .catch(() => {});
    api.get("/me/certificates")
      .then((r) => {
        const found = (r.data.certificates || []).find((c) => c.course_slug === courseSlug);
        setCert(found || null);
      })
      .catch(() => {});
  }, [courseSlug]);

  const submitRating = async (e) => {
    e.preventDefault();
    if (!stars) return;
    setSubmitting(true);
    try {
      await api.post(`/courses/${courseSlug}/rate`, { stars, comment });
      setRated(true);
      toast.success("Thanks for the rating!");
    } catch (err) {
      console.error(err);
      toast.error("Could not save your rating");
    } finally {
      setSubmitting(false);
    }
  };

  const shareCardUrl = cert ? `${API_BASE}/certificates/share-card/${cert.id}` : null;
  const sharePageUrl = cert ? `${window.location.origin}/share/cert/${cert.id}` : null;
  // Social-bot scraper URL — backend-rendered HTML stub with proper og:image
  // tags. Bots see the right card; real users get redirected to the React
  // share page so the experience is identical.
  const ogShareUrl = cert ? `${API_BASE}/og/cert/${cert.id}` : null;
  const verifyUrl = cert ? `${window.location.origin}/verify/${cert.id}` : null;
  const shareText = course
    ? `I just completed ${course.title} at Freddy Compute Academy.`
    : "I just completed a course at Freddy Compute Academy.";

  // Social shares point at the backend OG URL (which carries og:image and
  // redirects users to /share/cert/:id on click).
  const linkedinHref = ogShareUrl
    ? `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(ogShareUrl)}`
    : "#";
  const facebookHref = ogShareUrl
    ? `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(ogShareUrl)}`
    : "#";
  const twitterHref = ogShareUrl
    ? `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(ogShareUrl)}`
    : "#";

  const onInstagram = async () => {
    if (!cert) return;
    try {
      await navigator.clipboard.writeText(`${shareText}\n${sharePageUrl || ""}`);
    } catch (e) { /* clipboard might be blocked */ }
    try {
      await downloadFromApi(`/certificates/share-card/${cert.id}`, `freddy-share-${cert.id.slice(0, 8)}.png`);
      toast.success("Caption copied! Share card downloaded — open Instagram to post.");
    } catch (e) {
      console.error("instagram share", e);
      toast.error("Could not download share card");
    }
  };

  const onDownloadCard = async () => {
    if (!cert) return;
    try {
      await downloadFromApi(`/certificates/share-card/${cert.id}`, `freddy-share-${cert.id.slice(0, 8)}.png`);
    } catch (e) {
      console.error("share card", e);
      toast.error("Could not download share card");
    }
  };

  const onDownloadPdf = async () => {
    if (!cert) return;
    try {
      await downloadFromApi(`/certificates/${cert.id}/pdf`, `Freddy-Compute-Academy-${courseSlug}.pdf`);
    } catch (e) {
      console.error("pdf download", e);
      toast.error("Could not download certificate PDF");
    }
  };

  return (
    <div className="min-h-screen bg-fc-navy text-white" data-testid="course-complete-page">
      <div className="max-w-5xl mx-auto px-6 lg:px-10 py-16 lg:py-24">
        <div className="text-center mb-12">
          <Trophy className="mx-auto text-fc-red mb-6" size={72} />
          <p className="fc-overline mb-3">COURSE COMPLETE</p>
          <h1 className="fc-h-hero text-5xl sm:text-6xl mb-4" data-testid="complete-title">
            <span className="text-fc-red">+500 XP.</span> Banked.
          </h1>
          {course && (
            <p className="text-white/70 text-lg max-w-2xl mx-auto">
              You completed <strong className="text-white">{course.title}</strong> with{" "}
              <strong className="text-white">{course.instructor?.name}</strong>.
            </p>
          )}
        </div>

        {/* Share card preview */}
        {shareCardUrl && (
          <div className="mb-12 max-w-3xl mx-auto">
            <p className="fc-overline mb-3 text-center">SHARE CARD</p>
            <img src={shareCardUrl} alt="Share card" className="w-full border border-white/10 shadow-2xl" />
            <div className="mt-5 flex flex-wrap justify-center gap-3" data-testid="share-buttons">
              <a href={linkedinHref} target="_blank" rel="noopener noreferrer" className="btn-fc-outline text-xs py-2.5 px-4" data-testid="share-linkedin">
                <Linkedin size={14} /> LinkedIn
              </a>
              <a href={facebookHref} target="_blank" rel="noopener noreferrer" className="btn-fc-outline text-xs py-2.5 px-4" data-testid="share-facebook">
                <Facebook size={14} /> Facebook
              </a>
              <a href={twitterHref} target="_blank" rel="noopener noreferrer" className="btn-fc-outline text-xs py-2.5 px-4" data-testid="share-twitter">
                <Twitter size={14} /> X / Twitter
              </a>
              <button onClick={onInstagram} className="btn-fc-outline text-xs py-2.5 px-4" data-testid="share-instagram">
                <Instagram size={14} /> Instagram
              </button>
              <button onClick={onDownloadCard} className="btn-fc-outline text-xs py-2.5 px-4" data-testid="download-share-card">
                <ImageIcon size={14} /> Download share card
              </button>
              <button onClick={onDownloadPdf} className="btn-fc-red text-xs py-2.5 px-4" data-testid="download-pdf">
                <Download size={14} /> Download Certificate (PDF)
              </button>
            </div>
          </div>
        )}

        {/* Rating */}
        {!rated ? (
          <form onSubmit={submitRating} className="bg-[#060A24] border border-white/10 p-8 max-w-2xl mx-auto" data-testid="rating-form">
            <p className="fc-overline mb-3">RATE THIS COURSE</p>
            <div className="flex items-center gap-2 mb-5">
              {[1, 2, 3, 4, 5].map((n) => (
                <button
                  key={n}
                  type="button"
                  onClick={() => setStars(n)}
                  className="transition-transform hover:scale-110"
                  data-testid={`star-${n}`}
                >
                  <Star size={28} className={n <= stars ? "text-[#F59E0B] fill-[#F59E0B]" : "text-white/25"} />
                </button>
              ))}
            </div>
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="What stuck with you from this course? (optional)"
              className="w-full bg-fc-navy border border-white/10 focus:border-fc-red text-white p-3 outline-none text-sm leading-relaxed min-h-[100px]"
              data-testid="rating-comment"
            />
            <button type="submit" disabled={!stars || submitting} className="btn-fc-red mt-5" data-testid="rating-submit">
              {submitting ? "Saving…" : "Submit Rating"}
            </button>
          </form>
        ) : (
          <div className="bg-[#10B981]/10 border border-[#10B981]/40 text-[#10B981] p-5 max-w-2xl mx-auto text-center">
            <Award className="inline mr-2" size={20} /> Rating saved.
          </div>
        )}

        <div className="mt-12 flex flex-wrap justify-center gap-3">
          <button onClick={() => navigate("/dashboard")} className="btn-fc-red" data-testid="complete-go-dashboard">
            Back to Dashboard <ArrowRight size={16} />
          </button>
          <button onClick={() => navigate("/courses")} className="btn-fc-outline">Pick the next course</button>
        </div>
      </div>
    </div>
  );
}
